import { useEffect, useRef } from 'react'
import { css } from '@firebolt-dev/css'

// --- Monaco loader (singleton) ---

let monacoPromise

function loadMonaco() {
  if (monacoPromise) return monacoPromise

  monacoPromise = new Promise(async resolve => {
    // If already loaded, just use it
    if (window.monaco && window.monaco.editor) {
      resolve(window.monaco)
      return
    }

    // Configure AMD loader path
    window.require = window.require || {}
    window.require.paths = window.require.paths || {}
    window.require.paths.vs =
      window.require.paths.vs || 'https://cdnjs.cloudflare.com/ajax/libs/monaco-editor/0.49.0/min/vs'

    // Load loader.js
    await new Promise(resolveScript => {
      const script = document.createElement('script')
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/monaco-editor/0.49.0/min/vs/loader.js'
      script.onload = () => resolveScript()
      document.head.appendChild(script)
    })

    // Load editor main
    await new Promise(resolveEditor => {
      window.require(['vs/editor/editor.main'], () => {
        resolveEditor()
      })
    })

    const monaco = window.monaco
    monaco.editor.defineTheme('default', darkPlusTheme)
    monaco.editor.setTheme('default')

    resolve(monaco)
  })

  return monacoPromise
}

// --- Theme (Dark+) ---

const darkPlusTheme = {
  inherit: true,
  base: 'vs-dark',
  rules: [
    { foreground: '#DCDCAA', token: 'entity.name.function' },
    { foreground: '#DCDCAA', token: 'support.function' },
    { foreground: '#DCDCAA', token: 'support.constant.handlebars' },
    { foreground: '#DCDCAA', token: 'source.powershell variable.other.member' },
    { foreground: '#DCDCAA', token: 'entity.name.operator.custom-literal' },
    { foreground: '#4EC9B0', token: 'meta.return-type' },
    { foreground: '#4EC9B0', token: 'support.class' },
    { foreground: '#4EC9B0', token: 'support.type' },
    { foreground: '#4EC9B0', token: 'entity.name.type' },
    { foreground: '#4EC9B0', token: 'entity.name.namespace' },
    { foreground: '#4EC9B0', token: 'entity.other.attribute' },
    { foreground: '#4EC9B0', token: 'entity.name.scope-resolution' },
    { foreground: '#4EC9B0', token: 'entity.name.class' },
    { foreground: '#4EC9B0', token: 'meta.type.cast.expr' },
    { foreground: '#4EC9B0', token: 'meta.type.new.expr' },
    { foreground: '#4EC9B0', token: 'support.constant.math' },
    { foreground: '#4EC9B0', token: 'support.constant.dom' },
    { foreground: '#4EC9B0', token: 'support.constant.json' },
    { foreground: '#4EC9B0', token: 'entity.other.inherited-class' },
    { foreground: '#C586C0', token: 'keyword.control' },
    { foreground: '#C586C0', token: 'keyword.other.using' },
    { foreground: '#C586C0', token: 'keyword.other.operator' },
    { foreground: '#C586C0', token: 'entity.name.operator' },
    { foreground: '#9CDCFE', token: 'variable' },
    { foreground: '#9CDCFE', token: 'meta.definition.variable.name' },
    { foreground: '#9CDCFE', token: 'support.variable' },
    { foreground: '#9CDCFE', token: 'entity.name.variable' },
    { foreground: '#4FC1FF', token: 'variable.other.constant' },
    { foreground: '#4FC1FF', token: 'variable.other.enummember' },
    { foreground: '#9CDCFE', token: 'meta.object-literal.key' },
    { foreground: '#CE9178', token: 'support.constant.property-value' },
    { foreground: '#CE9178', token: 'support.constant.font-name' },
    { foreground: '#CE9178', token: 'support.constant.media-type' },
    { foreground: '#CE9178', token: 'support.constant.media' },
    { foreground: '#CE9178', token: 'constant.other.color.rgb-value' },
    { foreground: '#CE9178', token: 'constant.other.rgb-value' },
    { foreground: '#CE9178', token: 'support.constant.color' },
    { foreground: '#569CD6', token: 'constant.character' },
    { foreground: '#d7ba7d', token: 'constant.character.escape' },
    { foreground: '#C8C8C8', token: 'entity.name.label' },
    { foreground: '#569CD6', token: 'constant.language' },
    { foreground: '#569CD6', token: 'entity.name.tag' },
    { foreground: '#569cd6', token: 'storage' },
  ],
  colors: {
    'editor.background': '#00000000', // transparent to let parent show
  },
  encodedTokensColors: [],
}

// --- Editor component ---

export function UICodeEditor({
  value,
  onChange,
  language = 'javascript',
  fontSize = 12,
  onSave, // optional – Ctrl/Cmd+S
}) {
  const mountRef = useRef(null)
  const editorRef = useRef(null)
  const valueRef = useRef(value)

  // Keep ref in sync with prop
  useEffect(() => {
    valueRef.current = value
    if (editorRef.current && editorRef.current.getValue() !== value) {
      editorRef.current.setValue(value || '')
    }
  }, [value])

  // Create editor once
  useEffect(() => {
    let disposed = false

    loadMonaco().then(monaco => {
      if (disposed || !mountRef.current) return

      const model = monaco.editor.createModel(value || '', language)

      const editor = monaco.editor.create(mountRef.current, {
        model,
        language,
        automaticLayout: true,
        scrollBeyondLastLine: true,
        lineNumbers: 'on',
        minimap: { enabled: false },
        tabSize: 2,
        insertSpaces: true,
        fontSize,
      })

      editorRef.current = editor

      editor.onDidChangeModelContent(() => {
        const newValue = editor.getValue()
        if (newValue !== valueRef.current) {
          valueRef.current = newValue
          if (onChange) onChange(newValue)
        }
      })

      if (onSave) {
        editor.addAction({
          id: 'save',
          label: 'Save',
          keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS],
          run: onSave,
        })
      }
    })

    return () => {
      disposed = true
      if (editorRef.current) {
        editorRef.current.dispose()
        editorRef.current = null
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Update font size when prop changes
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.updateOptions({ fontSize })
    }
  }, [fontSize])

  return (
    <div
      className='editor'
      css={css`
        flex: 1;
        position: relative;
        overflow: hidden;
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;

        .editor-mount {
          position: absolute;
          inset: 0;
        }

        .monaco-editor {
          --vscode-focusBorder: #00000000 !important;
        }
      `}
    >
      <div className='editor-mount' ref={mountRef} />
    </div>
  )
}
